import { computed, ref } from "vue";
import { showAlert, showConfirm } from "@erp/shared/utils/dialog";
import { BLANK_ROW_COLSPAN } from "../const";
import { addEmptyRow } from "@erp/shared/components/ui/Document/_utils/calc-cost-row";
import { cloneDeep } from "es-toolkit";
// ── 상수 ───────────────────────────────────────────────────
/** 제목 행의 colspan (단위·수량·단가 컬럼을 항목에 병합) */
const TITLE_ROW_COLSPAN = 4;
/** 일반 행의 colspan */
const ROW_COLSPAN = 1;
/** 테이블 최대 깊이 (부모 → 자식 → 손자) */
const MAX_TABLE_DEPTH = 3;
/** 같은 깊이에서 추가 가능한 최대 행 수 */
const MAX_TABLE_ROW = 13;
// ── 컴포저블 ───────────────────────────────────────────────
export const useDirectInputCost = () => {
    // ── 상태 ─────────────────────────────────────────────────
    const calcCostDirectInputData = ref([addEmptyRow()]);
    const directInputValidate = ref(false);
    /** 사용자가 직접 입력하는 총 합계 (숫자로 저장, 뷰에서만 콤마 포맷 표시) */
    const manualTotalCost = ref(0);
    // ── 유효성 검증 (콜백 패턴) ──────────────────────────────
    //
    // 전송 버튼 클릭 시에만 v-form.validate()를 호출하기 위해
    // DirectInputCostTable 컴포넌트가 콜백을 등록하고,
    // 서비스 레이어(useEstimateService 등)에서 triggerDirectInputValidation()으로 트리거
    //
    const _triggerValidationFn = ref(null);
    /** DirectInputCostTable 컴포넌트에서 validate 콜백 등록/해제 */
    const registerTriggerValidation = (fn) => {
        _triggerValidationFn.value = fn;
    };
    /** 서비스 레이어에서 호출 → 폼 에러 표시 트리거 + 유효성 결과 반환 */
    const triggerDirectInputValidation = () => {
        return _triggerValidationFn.value?.() ?? false;
    };
    // ── 합계 관리 ────────────────────────────────────────────
    const directInputTotalCost = computed({
        get: () => manualTotalCost.value,
        set: (val) => {
            manualTotalCost.value = val;
        },
    });
    const setManualTotalCost = (val) => {
        manualTotalCost.value = val;
    };
    // ── 행 조작 ──────────────────────────────────────────────
    /** 빈 행 생성 (구분선 역할, 번호·단위·수량·단가 없음) */
    const createBlankRow = () => {
        return {
            cost_list: "",
            cost_list_colspan: BLANK_ROW_COLSPAN,
            cost_unit: "",
            cost_num: null,
            cost_unit_price: null,
            cost_total: "",
            belong_data: [],
        };
    };
    /** 현재 행 아래에 빈 행 추가 */
    const addBlankRow = (index, depth, parent) => {
        const newRow = createBlankRow();
        if (depth === 1) {
            if (calcCostDirectInputData.value.length >= MAX_TABLE_ROW) {
                showAlert("행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            calcCostDirectInputData.value.splice(index + 1, 0, newRow);
        }
        else if ((depth === 2 || depth === 3) && parent?.belong_data) {
            if (parent.belong_data.length >= MAX_TABLE_ROW) {
                showAlert("하위 행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            parent.belong_data.splice(index + 1, 0, newRow);
        }
    };
    /** 현재 행 아래에 형제 행 추가 */
    const addSiblingRow = (index, depth, parent) => {
        const newRow = addEmptyRow("", ROW_COLSPAN);
        if (depth === 1) {
            if (calcCostDirectInputData.value.length >= MAX_TABLE_ROW) {
                showAlert("행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            calcCostDirectInputData.value.splice(index + 1, 0, newRow);
        }
        else if ((depth === 2 || depth === 3) && parent?.belong_data) {
            if (parent.belong_data.length >= MAX_TABLE_ROW) {
                showAlert("하위 행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            parent.belong_data.splice(index + 1, 0, newRow);
        }
    };
    /** 현재 행의 자식으로 하위 행 추가 (depth 2까지만 가능) */
    const addChildRow = (index, depth, parent) => {
        const newChildRow = addEmptyRow("", ROW_COLSPAN);
        if (depth === 1) {
            if (calcCostDirectInputData.value.length >= MAX_TABLE_ROW) {
                showAlert("행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            calcCostDirectInputData.value[index].belong_data?.push(newChildRow);
        }
        else if (depth === 2 && parent?.belong_data) {
            const child = parent.belong_data[index];
            if (!child) {
                console.warn("하위 행이 존재하지 않습니다.");
                return;
            }
            if (!child.belong_data)
                child.belong_data = [];
            if (child.belong_data.length >= MAX_TABLE_ROW) {
                showAlert("하위 행은 13개 이상 추가할 수 없습니다.");
                return;
            }
            child.belong_data.push(newChildRow);
        }
    };
    /** 행 삭제 (하위 행이 있으면 확인 다이얼로그 표시) */
    const deleteRow = (idx, depth, parent) => {
        if (depth === 1 && calcCostDirectInputData.value.length === 1) {
            showAlert("행은 하나 이상 존재해야 합니다.");
            return;
        }
        if (depth !== MAX_TABLE_DEPTH &&
            parent?.belong_data &&
            parent.belong_data.length > 0) {
            showConfirm("삭제 시 하위 행도 삭제됩니다. 삭제하시겠습니까?", "삭제 확인", false, "확인", "취소").then((result) => {
                if (!result) {
                    return;
                }
                if (depth === 1) {
                    calcCostDirectInputData.value.splice(idx, 1);
                }
                else if ((depth === 2 || depth === 3) && parent) {
                    parent.belong_data?.splice(idx, 1);
                }
            });
        }
        else {
            if (depth === 1) {
                calcCostDirectInputData.value.splice(idx, 1);
            }
            else if ((depth === 2 || depth === 3) && parent) {
                parent.belong_data?.splice(idx, 1);
            }
        }
    };
    // ── 행 타입 변환 ─────────────────────────────────────────
    /** 일반 행 → 제목 행으로 변환 (단위·수량·단가 컬럼 병합) */
    const convertToTitleRow = (item) => {
        const titleName = item.cost_list;
        const subRows = cloneDeep(item.belong_data || []);
        // 객체 초기화
        Object.assign(item, addEmptyRow("", TITLE_ROW_COLSPAN));
        item.cost_list = titleName;
        item.belong_data = subRows;
        return item;
    };
    /** 제목 행 → 일반 행으로 되돌리기 */
    const cancelTitleRow = (item) => {
        const originalName = item.cost_list;
        const originalSubRows = cloneDeep(item.belong_data || []);
        Object.assign(item, addEmptyRow("", ROW_COLSPAN));
        item.cost_list = originalName;
        item.belong_data = originalSubRows;
        return item;
    };
    // ── 하위 호환 ──────────────────────────────────────────
    /** @deprecated rowActions 사용 권장. OrderPaper(발주서)에서 아직 사용 중 */
    const getMenuItems = (item, index, depth, parent) => {
        const isTitleRow = item.cost_list_colspan === 4;
        return [
            {
                text: isTitleRow ? "되돌리기" : "제목 행으로 변경",
                click: () => {
                    if (isTitleRow) {
                        cancelTitleRow(item);
                    }
                    else {
                        convertToTitleRow(item);
                    }
                },
            },
            {
                text: "행 추가",
                click: () => {
                    addSiblingRow(index, depth, parent);
                },
            },
            {
                text: "빈 행 추가",
                click: () => {
                    addBlankRow(index, depth, parent);
                },
            },
            {
                text: "하위 행 추가",
                color: depth === MAX_TABLE_DEPTH ? "gray" : "black",
                disabled: depth === MAX_TABLE_DEPTH,
                click: () => {
                    addChildRow(index, depth, parent);
                },
            },
            {
                text: "행 삭제",
                color: "#db1d2c",
                click: () => {
                    deleteRow(index, depth, parent);
                },
            },
        ];
    };
    // ── 유효성·초기화·적용 ──────────────────────────────────
    const setDirectInputValidate = (value) => {
        directInputValidate.value = value;
    };
    /** 테이블 초기 상태로 리셋 */
    const resetTable = () => {
        calcCostDirectInputData.value = [addEmptyRow()];
        manualTotalCost.value = 0;
    };
    /** 기존 데이터 불러오기 (견적서/수주서 수정 시) */
    const applyDirectInputCost = (data, totalCost) => {
        if (!data)
            return;
        if (typeof data === "string") {
            calcCostDirectInputData.value = JSON.parse(data);
        }
        else {
            calcCostDirectInputData.value = data;
        }
        manualTotalCost.value =
            totalCost !== undefined ? Number(totalCost) || 0 : 0;
    };
    // ── 외부 노출 ────────────────────────────────────────────
    /** 행 조작 액션 객체 (DirectInputCostTable 컴포넌트에 전달) */
    const rowActions = {
        addSiblingRow,
        addBlankRow,
        addChildRow,
        deleteRow,
        convertToTitleRow,
        cancelTitleRow,
    };
    return {
        // 상태
        calcCostDirectInputData,
        directInputTotalCost,
        directInputValidate,
        // 행 조작
        rowActions,
        // @deprecated 개별 함수 (OrderPaper 하위 호환용)
        addSiblingRow,
        addChildRow,
        addBlankRow,
        deleteRow,
        convertToTitleRow,
        cancelTitleRow,
        getMenuItems,
        setDirectInputValidate,
        setManualTotalCost,
        resetTable,
        applyDirectInputCost,
        registerTriggerValidation,
        triggerDirectInputValidation,
    };
};
